import { useEffect, type ReactNode } from "react";
import { FieldArrayWithId, FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { CodedInput } from "@/modules/add-new-data/components/coded-input";
import { ScrollableFormShell } from "@/modules/add-new-data/components/scrollable-form-shell";
import { resolveMemberAge } from "@/modules/add-new-data/lib/age";
import { getApplicabilityForQuestion, notApplicableValue, type MemberFieldName } from "@/modules/add-new-data/lib/cleansing";
import {
  composeLengthOfStay,
  composeResidence,
  deriveResidentTypeCode,
  isLengthOfStayAtLeastFiveYears,
  normalizeNumericPart,
  parseLengthOfStay,
  parseResidence,
} from "@/modules/add-new-data/lib/questionnaire-split-values";
import { AddNewDataFormValues } from "@/modules/add-new-data/lib/schema";
import { cleanRBIMCode, type RbimCodeQuestionKey } from "@/lib/rbimCodeMap";
import { cn } from "@/lib/utils";

type RbimHealthMigrationPagesStepProps = {
  errors: FieldErrors<AddNewDataFormValues>;
  fields: FieldArrayWithId<AddNewDataFormValues, "members", "id">[];
  watchedMembers: AddNewDataFormValues["members"];
  register: UseFormRegister<AddNewDataFormValues>;
  setValue: UseFormSetValue<AddNewDataFormValues>;
};

const cellInput =
  "h-8 rounded-none border-0 bg-white px-1.5 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-emerald-700 focus-visible:ring-offset-0";

export function RbimHealthMigrationPagesStep({
  errors,
  fields,
  watchedMembers,
  register,
  setValue,
}: RbimHealthMigrationPagesStepProps) {
  useEffect(() => {
    const migrationFields = [
      "lengthOfStayInBarangay",
      "previousResidenceFiveYears",
      "previousResidenceSixMonths",
      "residentType",
    ] as const;

    watchedMembers.forEach((member, index) => {
      if (!member) {
        return;
      }

      /*
       * Q34-Q37 apply only to persons aged 5 and above.
       * Infants and children aged 0-4 must always carry
       * the system-generated 99 - N/A value.
       */
      const shouldBeNotApplicable =
        isMigrationUnderFive(member);

      migrationFields.forEach((field) => {
        const currentValue =
          String(member[field] ?? "").trim();

        const fieldPath =
          `members.${index}.${field}`;

        if (shouldBeNotApplicable) {
          if (
            currentValue.toUpperCase() !==
            notApplicableValue.toUpperCase()
          ) {
            setValue(
              fieldPath as Parameters<typeof setValue>[0],
              notApplicableValue as never,
              {
                shouldDirty: true,
                shouldTouch: false,
                shouldValidate: false,
              },
            );
          }

          return;
        }

        /*
         * If DOB/age changes and the person becomes 5+,
         * remove only the system-generated N/A so the
         * encoder can answer Q34-Q37 normally.
         */
        if (
          !shouldBeNotApplicable &&
          hasKnownMigrationAge(member) &&
          currentValue.toUpperCase() ===
            notApplicableValue.toUpperCase()
        ) {
          setValue(
            fieldPath as Parameters<typeof setValue>[0],
            "" as never,
            {
              shouldDirty: true,
              shouldTouch: false,
              shouldValidate: false,
            },
          );
        }
      });
    });
  }, [setValue, watchedMembers]);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border bg-white p-4 shadow-sm">
        <p className="text-sm font-semibold text-slate-950">
          Pages 6 - 7: Health Information, Socio-Civic Participation, and Migration
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Continue the RBIM line list for Q27 to Q37. Scroll sideways to complete each member row.
        </p>
      </div>

      <ScrollableFormShell minWidthClassName="min-w-[3260px]">
        <table className="w-full table-fixed border-collapse text-center text-xs text-slate-950">
          <colgroup>
            <col className="w-[72px]" />
            <col className="w-[300px]" />
            <col className="w-[105px]" />
            <col className="w-[95px]" />
            <col className="w-[205px]" />
            <col className="w-[220px]" />
            <col className="w-[220px]" />
            <col className="w-[210px]" />
            <col className="w-[190px]" />
            <col className="w-[170px]" />
            <col className="w-[210px]" />
            <col className="w-[100px]" />
            <col className="w-[100px]" />
            <col className="w-[150px]" />
            <col className="w-[150px]" />
            <col className="w-[150px]" />
            <col className="w-[150px]" />
            <col className="w-[180px]" />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={5} className="border border-black bg-[#eeeeee] px-2 py-2 align-bottom font-medium">
                LINE<br />LIST<br />NO.
              </th>
              <th colSpan={3} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                A. Demographic Characteristics
              </th>
              <th colSpan={4} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                C. Health Information
              </th>
              <th colSpan={3} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                D. Socio-Civic Participation
              </th>
              <th colSpan={7} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                E. Migration Information
              </th>
            </tr>
            <tr>
              <th colSpan={3} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For All Household Members
              </th>
              <th colSpan={4} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For All Household Members
              </th>
              <th className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">For 10 & Above</th>
              <th className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">For 60 & Above</th>
              <th className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">For 15 & Above</th>
              <th colSpan={7} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For 5 & Above
              </th>
            </tr>
            <tr>
              <QuestionHeader code="Q1" title="Name" />
              <QuestionHeader code="Q3" title="Sex" />
              <QuestionHeader code="Q4" title="Age" />
              <QuestionHeader code="Q27" title="Health Insurance" />
              <QuestionHeader code="Q28" title="Facility Visited in the Past 12 Months" />
              <QuestionHeader code="Q29" title="Reason for Visit to the Health Facility" />
              <QuestionHeader code="Q30" title="Disability" />
              <QuestionHeader code="Q31" title="Solo Parent" />
              <QuestionHeader code="Q32" title="Registered Senior Citizen" />
              <QuestionHeader code="Q33" title="Registered Voter" />
              <QuestionHeader code="Q34" title="Length of Stay in the Barangay" colSpan={2} />
              <QuestionHeader code="Q35" title="Previous Residence 5 Years Ago" colSpan={2} />
              <QuestionHeader code="Q36" title="Previous Residence 6 Months Ago" colSpan={2} />
              <QuestionHeader code="Q37" title="Type Resident" />
            </tr>
            <tr>
              <Instruction>Copy from previous response</Instruction>
              <Instruction />
              <Instruction />
              <Instruction>What is the primary health insurance ___ have?</Instruction>
              <Instruction>What facility did ___ visit in the past 12 months?</Instruction>
              <Instruction>What is the reason for the visit in the health facility?</Instruction>
              <Instruction>Is there a member of the HH that has any disability? What is the disability?</Instruction>
              <Instruction>Is there a member of the HH that is a solo parent? Is he/she registered?</Instruction>
              <Instruction>Is ___ a registered senior citizen?</Instruction>
              <Instruction>Is _____ a registered voter? If yes, please specify the barangay where _____ is registered.</Instruction>
              <Instruction colSpan={2}>How long has _____ been staying in this barangay?</Instruction>
              <Instruction colSpan={2}>In what barangay and city/municipality did the person reside five years ago?</Instruction>
              <Instruction colSpan={2}>In what barangay and city/municipality did the person reside six months ago?</Instruction>
              <Instruction>Do not ask. Indicate if Non-migrant, Migrant, or Transient.</Instruction>
            </tr>
            <tr>
              {Array.from({ length: 10 }, (_, index) => <SubHeader key={`blank-${index}`} />)}
              <SubHeader>Years</SubHeader>
              <SubHeader>Months</SubHeader>
              <SubHeader>Barangay</SubHeader>
              <SubHeader>City/Mun.</SubHeader>
              <SubHeader>Barangay</SubHeader>
              <SubHeader>City/Mun.</SubHeader>
              <SubHeader />
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => {
              const member = watchedMembers[index];
              const memberErrors = errors.members?.[index];
              const resolvedAge = resolveMemberAge(member ?? {});
              const age = resolvedAge.ageDisplay || null;
              const migrationAgeNotApplicable =
                member ? isMigrationUnderFive(member) : false;
              const name = [member?.lastName, member?.firstName, member?.middleName].filter(Boolean).join(", ");
              const q27Other = shouldShowDetail(member?.healthInsurance, "q27_health_insurance", "8");
              const q28Other = shouldShowDetail(member?.facilityVisitedPast12Months, "q28_facility_visited", "8");
              const q29Other = shouldShowDetail(member?.healthFacilityVisitReason, "q29_health_facility_visit_reason", "8");

              return (
                <tr key={field.id}>
                  <LineCell>{index + 1}</LineCell>
                  <ReadOnlyCell>{name || "Copy from Q1"}</ReadOnlyCell>
                  <ReadOnlyCell>{member?.sex || ""}</ReadOnlyCell>
                  <ReadOnlyCell>{age === null ? "" : age}</ReadOnlyCell>
                  <CodeCell
                    questionKey="q27_health_insurance"
                    error={memberErrors?.healthInsurance?.message ?? memberErrors?.healthInsuranceOther?.message}
                    register={register(`members.${index}.healthInsurance`)}
                    detail={q27Other ? register(`members.${index}.healthInsuranceOther`) : undefined}
                    detailError={memberErrors?.healthInsuranceOther?.message}
                    value={member?.healthInsurance}
                  />
                  <CodeCell
                    questionKey="q28_facility_visited"
                    error={memberErrors?.facilityVisitedPast12Months?.message ?? memberErrors?.facilityVisitedPast12MonthsOther?.message}
                    register={register(`members.${index}.facilityVisitedPast12Months`)}
                    detail={q28Other ? register(`members.${index}.facilityVisitedPast12MonthsOther`) : undefined}
                    detailError={memberErrors?.facilityVisitedPast12MonthsOther?.message}
                    value={member?.facilityVisitedPast12Months}
                  />
                  <CodeCell
                    questionKey="q29_health_facility_visit_reason"
                    error={memberErrors?.healthFacilityVisitReason?.message ?? memberErrors?.healthFacilityVisitReasonOther?.message}
                    isSkipped={isSkipped(member, "healthFacilityVisitReason")}
                    register={register(`members.${index}.healthFacilityVisitReason`)}
                    detail={q29Other ? register(`members.${index}.healthFacilityVisitReasonOther`) : undefined}
                    detailError={memberErrors?.healthFacilityVisitReasonOther?.message}
                    value={member?.healthFacilityVisitReason}
                  />
                  <CodeCell
                    questionKey="q30_disability"
                    error={memberErrors?.disability?.message}
                    isSkipped={isSkipped(member, "disability")}
                    register={register(`members.${index}.disability`)}
                    value={member?.disability}
                  />
                  <CodeCell
                    questionKey="q31_solo_parent"
                    error={memberErrors?.soloParentStatus?.message}
                    isSkipped={isSkipped(member, "soloParentStatus")}
                    register={register(`members.${index}.soloParentStatus`)}
                    value={member?.soloParentStatus}
                  />
                  <CodeCell
                    questionKey="q32_registered_senior_citizen"
                    error={memberErrors?.registeredSeniorCitizen?.message}
                    isSkipped={isSkipped(member, "registeredSeniorCitizen")}
                    register={register(`members.${index}.registeredSeniorCitizen`)}
                    value={member?.registeredSeniorCitizen}
                  />
                  <InputCell error={memberErrors?.registeredVoterBarangay?.message}>
                    {isSkipped(member, "registeredVoterBarangay") ? (
                      <SkippedInput ariaLabel={`Line ${index + 1} Q33 not applicable`} />
                    ) : (
                      (() => {
                        const q33 = parseRegisteredVoterAnswer(
                          member?.registeredVoterBarangay,
                        );
                        const fieldName =
                          `members.${index}.registeredVoterBarangay` as const;

                        return (
                          <div className="grid gap-1">
                            <input type="hidden" {...register(fieldName)} />

                            <select
                              aria-label={`Line ${index + 1} Q33 registered voter`}
                              className={cellInput}
                              data-grid-input="true"
                              value={q33.status}
                              onChange={(event) => {
                                const nextStatus = event.currentTarget.value;

                                const nextValue =
                                  nextStatus === "1"
                                    ? "1 - Yes | "
                                    : nextStatus === "2"
                                      ? "2 - No | 99 - N/A"
                                      : "";

                                setValue(fieldName, nextValue, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                });
                              }}
                            >
                              <option value="">Select</option>
                              <option value="1">1 - Yes</option>
                              <option value="2">2 - No</option>
                            </select>

                            {q33.status === "1" ? (
                              <Input
                                aria-label={`Line ${index + 1} Q33 registered barangay`}
                                autoUppercase
                                className={cellInput}
                                data-grid-input="true"
                                placeholder="Barangay"
                                value={q33.barangay}
                                onChange={(event) => {
                                  const barangay =
                                    event.currentTarget.value.toUpperCase();

                                  setValue(
                                    fieldName,
                                    `1 - Yes | ${barangay}`,
                                    {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                              />
                            ) : null}

                            {q33.status === "2" ? (
                              <Input
                                aria-label={`Line ${index + 1} Q33 barangay not applicable`}
                                className={cellInput}
                                data-grid-input="true"
                                readOnly
                                value="99 - N/A"
                              />
                            ) : null}
                          </div>
                        );
                      })()
                    )}
                  </InputCell>
                  <LengthOfStayCells
                    error={memberErrors?.lengthOfStayInBarangay?.message}
                    isSkipped={migrationAgeNotApplicable || isSkipped(member, "lengthOfStayInBarangay")}
                    register={register(`members.${index}.lengthOfStayInBarangay`)}
                    setValue={setValue}
                    value={member?.lengthOfStayInBarangay}
                  />
                  <ResidenceCells
                    ariaLabelPrefix={`Line ${index + 1} Q35`}
                    error={memberErrors?.previousResidenceFiveYears?.message}
                    isSkipped={migrationAgeNotApplicable || isSkipped(member, "previousResidenceFiveYears")}
                    readOnly={isLengthOfStayAtLeastFiveYears(member?.lengthOfStayInBarangay)}
                    register={register(`members.${index}.previousResidenceFiveYears`)}
                    setValue={setValue}
                    value={member?.previousResidenceFiveYears}
                  />
                  <ResidenceCells
                    ariaLabelPrefix={`Line ${index + 1} Q36`}
                    error={memberErrors?.previousResidenceSixMonths?.message}
                    isSkipped={migrationAgeNotApplicable || isSkipped(member, "previousResidenceSixMonths")}
                    readOnly={isLengthOfStayAtLeastFiveYears(member?.lengthOfStayInBarangay)}
                    register={register(`members.${index}.previousResidenceSixMonths`)}
                    setValue={setValue}
                    value={member?.previousResidenceSixMonths}
                  />
                  <CodeCell
                    disabled={Boolean(deriveResidentTypeCode(member?.lengthOfStayInBarangay))}
                    questionKey="q37_resident_type"
                    error={memberErrors?.residentType?.message}
                    isSkipped={migrationAgeNotApplicable || isSkipped(member, "residentType")}
                    register={register(`members.${index}.residentType`)}
                    value={member?.residentType}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollableFormShell>
    </div>
  );
}

function isMigrationUnderFive(
  member: AddNewDataFormValues["members"][number],
) {
  const resolvedAge = resolveMemberAge(member);

  /*
   * maximumAgeYears is preferred when age is approximate.
   * This prevents an uncertain 4/5-year-old estimate from
   * being incorrectly forced to N/A.
   */
  if (resolvedAge.maximumAgeYears !== null) {
    return resolvedAge.maximumAgeYears <= 4;
  }

  return (
    resolvedAge.ageYears !== null &&
    resolvedAge.ageYears <= 4
  );
}

function hasKnownMigrationAge(
  member: AddNewDataFormValues["members"][number],
) {
  const resolvedAge = resolveMemberAge(member);

  return (
    resolvedAge.ageYears !== null ||
    resolvedAge.minimumAgeYears !== null ||
    resolvedAge.maximumAgeYears !== null
  );
}

type RegisteredVoterAnswer = {
  status: "" | "1" | "2";
  barangay: string;
};

function parseRegisteredVoterAnswer(value: string | undefined): RegisteredVoterAnswer {
  /*
   * Preserve the barangay exactly while the encoder is typing.
   *
   * In particular, do not trim the raw value here. A controlled
   * input must retain the trailing space after words such as
   * "BAGONG " so the encoder can continue typing "DAWIS".
   */
  const raw = String(value ?? "");
  const trimmed = raw.trim();

  if (!trimmed) {
    return { status: "", barangay: "" };
  }

  if (
    /^99\s*-\s*N\/A$/i.test(trimmed) ||
    /^2\s*-\s*No\s*\|\s*99\s*-\s*N\/A$/i.test(trimmed)
  ) {
    return {
      status: "2",
      barangay: "99 - N/A",
    };
  }

  /*
   * Consume only the single separator space generated by RBIM
   * after "|". Everything after that belongs to the barangay
   * and must be preserved exactly.
   */
  const yesMatch =
    /^1\s*-\s*Yes(?:\s*\|\s?(.*))?$/i.exec(raw);

  if (yesMatch) {
    return {
      status: "1",
      barangay: String(yesMatch[1] ?? ""),
    };
  }

  // Backward compatibility:
  // an existing plain barangay value represents a registered voter.
  return {
    status: "1",
    barangay: raw,
  };
}
function QuestionHeader({
  code,
  title,
  children,
  className,
  colSpan,
}: {
  code: string;
  title: string;
  children?: ReactNode;
  className?: string;
  colSpan?: number;
}) {
  return (
    <th colSpan={colSpan} className={cn("border border-black px-2 py-3 align-middle font-medium uppercase", className)}>
      <span className="block font-bold">{code}</span>
      <span className="block">{title}</span>
      {children && <span className="mt-1 block text-[11px] font-normal normal-case leading-tight">{children}</span>}
    </th>
  );
}

function Instruction({ children, colSpan }: { children?: ReactNode; colSpan?: number }) {
  return <th colSpan={colSpan} className="border border-black px-2 py-3 align-top text-[11px] font-normal leading-tight">{children}</th>;
}

function SubHeader({ children }: { children?: ReactNode }) {
  return (
    <th className="h-5 border border-black bg-[#eeeeee] px-1 py-1 text-[9px] font-bold uppercase leading-none text-slate-700">
      {children}
    </th>
  );
}

function LineCell({ children }: { children: ReactNode }) {
  return <td className="border border-black bg-white px-2 py-1 align-middle font-semibold">{children}</td>;
}

function ReadOnlyCell({ children }: { children: ReactNode }) {
  return <td className="border border-black bg-white px-2 py-1 text-left align-middle text-xs font-medium">{children}</td>;
}

function InputCell({
  children,
  colSpan,
  error,
  showErrorMessage = true,
}: {
  children: ReactNode;
  colSpan?: number;
  error?: string;
  showErrorMessage?: boolean;
}) {
  return (
    <td colSpan={colSpan} className={cn("border border-black bg-white align-top", error && "bg-red-50 ring-1 ring-inset ring-red-500")}>
      {children}
      {error && showErrorMessage ? <p className="px-1 pb-1 text-left text-[10px] font-semibold text-red-700">{String(error)}</p> : null}
    </td>
  );
}

function SkippedInput({ ariaLabel }: { ariaLabel: string }) {
  return (
    <Input
      aria-label={ariaLabel}
      className={cn(cellInput, "text-center font-semibold text-slate-600")}
      readOnly
      tabIndex={-1}
      value={notApplicableValue}
    />
  );
}

function CodeCell({
  placeholder = "Code",
  questionKey,
  error,
  register,
  detail,
  detailError,
  disabled = false,
  isSkipped = false,
  value,
}: {
  placeholder?: string;
  questionKey: RbimCodeQuestionKey;
  error?: string;
  register: ReturnType<UseFormRegister<AddNewDataFormValues>>;
  detail?: ReturnType<UseFormRegister<AddNewDataFormValues>>;
  detailError?: string;
  disabled?: boolean;
  isSkipped?: boolean;
  value?: string;
}) {
  return (
    <InputCell error={error}>
      {isSkipped ? (
        <SkippedInput ariaLabel={`${questionKey} not applicable`} />
      ) : (
        <>
          <CodedInput
            className={cellInput}
            data-grid-input="true"
            disabled={disabled}
            placeholder={placeholder}
            questionKey={questionKey}
            registration={register}
            value={value}
          />
          {detail ? (
            <Input
              autoUppercase
              className={cn(cellInput, "border-t border-slate-300", detailError && "bg-red-50")}
              data-grid-input="true"
              placeholder="Specify"
              {...detail}
            />
          ) : null}
        </>
      )}
    </InputCell>
  );
}

function LengthOfStayCells({
  error,
  isSkipped,
  register,
  setValue,
  value,
}: {
  error?: string;
  isSkipped: boolean;
  register: ReturnType<UseFormRegister<AddNewDataFormValues>>;
  setValue: UseFormSetValue<AddNewDataFormValues>;
  value?: string;
}) {
  if (isSkipped) {
    return (
      <InputCell colSpan={2} error={error}>
        <SkippedInput ariaLabel="Q34 not applicable" />
      </InputCell>
    );
  }

  const parts = parseLengthOfStay(value);
  const updatePart = (part: "years" | "months", nextValue: string) => {
    writeCompositeValue(
      register,
      setValue,
      composeLengthOfStay({ ...parts, [part]: normalizeNumericPart(nextValue) }),
    );
  };

  return (
    <>
      <InputCell error={error} showErrorMessage={false}>
        <input type="hidden" {...register} />
        <Input
          aria-label="Q34 length of stay years"
          className={cn(cellInput, "text-center tabular-nums")}
          data-grid-input="true"
          inputMode="numeric"
          placeholder="0"
          value={parts.years}
          onChange={(event) => updatePart("years", event.currentTarget.value)}
        />
      </InputCell>
      <InputCell error={error}>
        <Input
          aria-label="Q34 length of stay months"
          className={cn(cellInput, "text-center tabular-nums")}
          data-grid-input="true"
          inputMode="numeric"
          placeholder="0"
          value={parts.months}
          onChange={(event) => updatePart("months", event.currentTarget.value)}
        />
      </InputCell>
    </>
  );
}

function ResidenceCells({
  ariaLabelPrefix,
  error,
  isSkipped,
  readOnly,
  register,
  setValue,
  value,
}: {
  ariaLabelPrefix: string;
  error?: string;
  isSkipped: boolean;
  readOnly: boolean;
  register: ReturnType<UseFormRegister<AddNewDataFormValues>>;
  setValue: UseFormSetValue<AddNewDataFormValues>;
  value?: string;
}) {
  if (isSkipped) {
    return (
      <InputCell colSpan={2} error={error}>
        <SkippedInput ariaLabel={`${ariaLabelPrefix} not applicable`} />
      </InputCell>
    );
  }

  const parts = parseResidence(value);
  const updatePart = (part: "barangay" | "cityMunicipality", nextValue: string) => {
    writeCompositeValue(register, setValue, composeResidence({ ...parts, [part]: nextValue }));
  };

  return (
    <>
      <InputCell error={error} showErrorMessage={false}>
        <input type="hidden" {...register} />
        <Input
          aria-label={`${ariaLabelPrefix} barangay`}
          autoUppercase
          className={cellInput}
          data-grid-input="true"
          placeholder="Barangay"
          readOnly={readOnly}
          value={parts.barangay}
          onChange={(event) => updatePart("barangay", event.currentTarget.value)}
        />
      </InputCell>
      <InputCell error={error}>
        <Input
          aria-label={`${ariaLabelPrefix} city or municipality`}
          autoUppercase
          className={cellInput}
          data-grid-input="true"
          placeholder="City/Mun."
          readOnly={readOnly}
          value={parts.cityMunicipality}
          onChange={(event) => updatePart("cityMunicipality", event.currentTarget.value)}
        />
      </InputCell>
    </>
  );
}

function writeCompositeValue(
  register: ReturnType<UseFormRegister<AddNewDataFormValues>>,
  setValue: UseFormSetValue<AddNewDataFormValues>,
  nextValue: string,
) {
  setValue(register.name as Parameters<typeof setValue>[0], nextValue as never, {
    shouldDirty: true,
    shouldTouch: false,
    shouldValidate: false,
  });
}

function isSkipped(member: AddNewDataFormValues["members"][number] | undefined, field: MemberFieldName) {
  return !member || getApplicabilityForQuestion(member, field) === "notApplicable";
}

function shouldShowDetail(
  value: string | undefined,
  questionKey: RbimCodeQuestionKey,
  code: string,
) {
  const result = cleanRBIMCode(questionKey, value);

  return !result.error && result.code === code && !hasInlineDetail(value, code);
}

function hasInlineDetail(value: string | undefined, code: string) {
  return new RegExp(`^0*${code}(?:\\s*(?:-|:|,)\\s*|\\s+)\\S+`, "i").test(String(value ?? "").trim());
}
